import { useCallback, useState } from 'react'

import {
    deleteInventoryAsset,
    getUnitInventory,
    InventoryAsset,
    InventoryAssetPatchPayload,
    PartialAttachPhotosError,
    saveInventoryAssetEdits,
    UnitInventoryResponse,
} from '../models'

export const useUnitInventory = () => {
    const [inventory, setInventory] = useState<UnitInventoryResponse | null>(
        null,
    )
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const loadInventory = useCallback(
        async (unitId: number, confirmed = true) => {
            setLoading(true)
            setError(null)
            try {
                const response = await getUnitInventory(unitId, { confirmed })
                setInventory(response.data)
                return response.data
            } catch (err) {
                console.error('Error loading unit inventory:', err)
                setInventory(null)
                setError('Unable to load inventory')
                return Promise.reject(err)
            } finally {
                setLoading(false)
            }
        },
        [],
    )

    const updateAssetInState = useCallback((updatedAsset: InventoryAsset) => {
        setInventory((prev) => {
            if (!prev) {
                return prev
            }

            const areas = Object.fromEntries(
                Object.entries(prev.areas).map(([areaKey, assets]) => [
                    areaKey,
                    assets.map((asset) =>
                        asset.id === updatedAsset.id ? updatedAsset : asset,
                    ),
                ]),
            )

            return { ...prev, areas }
        })
    }, [])

    const removeAssetFromState = useCallback((assetId: number) => {
        setInventory((prev) => {
            if (!prev) {
                return prev
            }

            let removed = 0
            const areas = Object.fromEntries(
                Object.entries(prev.areas).map(([areaKey, assets]) => {
                    const nextAssets = assets.filter((asset) => {
                        if (asset.id === assetId) {
                            removed += 1
                            return false
                        }
                        return true
                    })
                    return [areaKey, nextAssets]
                }),
            )

            return {
                ...prev,
                areas,
                total_assets: Math.max(0, prev.total_assets - removed),
            }
        })
    }, [])

    const saveAsset = useCallback(
        async (
            assetId: number,
            payload: InventoryAssetPatchPayload,
            options?: {
                useConfirmEndpoint?: boolean
                pendingPhotoFiles?: File[]
            },
        ) => {
            try {
                const updatedAsset = await saveInventoryAssetEdits(
                    assetId,
                    payload,
                    options,
                )
                updateAssetInState(updatedAsset)
                return updatedAsset
            } catch (error) {
                if (error instanceof PartialAttachPhotosError) {
                    updateAssetInState(error.asset)
                }
                throw error
            }
        },
        [updateAssetInState],
    )

    const deleteAsset = useCallback(
        async (assetId: number) => {
            await deleteInventoryAsset(assetId)
            removeAssetFromState(assetId)
        },
        [removeAssetFromState],
    )

    const clearInventory = useCallback(() => {
        setInventory(null)
        setError(null)
    }, [])

    return {
        inventory,
        loading,
        error,
        loadInventory,
        saveAsset,
        deleteAsset,
        updateAssetInState,
        clearInventory,
    }
}
